import { useCallback, useEffect, useState } from 'react';
import { IS_STAGING, IS_DEV_ENV } from '../env.js';
import { tradeApi } from './api.js';
import NewTicket from './NewTicket.jsx';
import RequestDetail from './RequestDetail.jsx';
import MyRequests from './MyRequests.jsx';
import ImQueue from './ImQueue.jsx';
import ImRequest from './ImRequest.jsx';
import './trade.css';

/**
 * The trade ticket: a separate part of the app under /trade, opened from the
 * "New in App" button on a household's Trade Requests list in Salesforce and
 * from the notification Salesforce sends when Investment Management sends a
 * request back. It has nothing to do with the PQ store, so it never loads a PQ.
 *
 *   /trade?householdId=<id>          a new request for that household
 *   /trade/requests                  the advisor's requests
 *   /trade/requests/<id>             one request and its conversation
 *   /trade/requests/<id>/replace     a corrected request in place of a sent-back one
 *   /trade/im                        Investment Management's queue (trading profiles only)
 *   /trade/im/requests/<id>          one request as a trader works it
 */
export function routeFromLocation(pathname, search) {
  const p = pathname.replace(/\/+$/, '');
  const m = p.match(/^\/trade\/requests\/([a-zA-Z0-9]{15,18})(\/replace)?$/);
  if (m) return { view: m[2] ? 'replace' : 'request', id: m[1] };
  if (p === '/trade/requests') return { view: 'requests' };
  const im = p.match(/^\/trade\/im\/requests\/([a-zA-Z0-9]{15,18})$/);
  if (im) return { view: 'imRequest', id: im[1] };
  if (p === '/trade/im') return { view: 'im' };
  const householdId = new URLSearchParams(search).get('householdId');
  if (householdId) return { view: 'new', householdId };
  return { view: 'requests' };
}

export function isTradePath(pathname) {
  return /^\/trade(\/|$)/i.test(pathname);
}

function Topbar({ me, context, navigate }) {
  const env = IS_STAGING ? ' is-staging' : IS_DEV_ENV ? ' is-dev' : '';
  const trader = Boolean(me && me.ok && me.body.isTrader);
  return (
    <header className={'topbar' + env}>
      <div className="topbar-inner">
        <a className="brand" href="/trade/requests" onClick={e => { e.preventDefault(); navigate('/trade/requests'); }}>
          <img className="brand-logo" src={IS_STAGING || IS_DEV_ENV ? '/pure-logo-onbrand.svg' : '/pure-logo.svg'} alt="Pure Financial Advisors" />
          <span className="brand-sep" />
          <span className="brand-context">
            <span className="brand-context-kicker">Trade ticket</span>
            <span className="brand-context-name">{context || 'Trade requests'}</span>
          </span>
        </a>
        <div className="top-actions">
          {IS_STAGING && <span className="pill pill-staging">Staging · Sandbox data</span>}
          {IS_DEV_ENV && <span className="pill pill-staging">Dev · Sandbox data</span>}
          {me && me.ok && (
            <span className="tt-whoami" title={`Salesforce user ${me.body.username}`}>
              In Salesforce as <strong>{me.body.name}</strong>
              {me.body.actingAs && <span className="tt-whoami-note"> (test user for {me.body.email})</span>}
            </span>
          )}
          {trader && <button className="btn btn-ghost-dark btn-sm" onClick={() => navigate('/trade/im')}>Investment Management</button>}
          <button className="btn btn-ghost-dark btn-sm" onClick={() => navigate('/trade/requests')}>My requests</button>
        </div>
      </div>
    </header>
  );
}

export default function TradeTicketApp() {
  const [route, setRoute] = useState(() => routeFromLocation(window.location.pathname, window.location.search));
  const [me, setMe] = useState(null);
  const [context, setContext] = useState('');
  const [replacing, setReplacing] = useState(null);

  const navigate = useCallback(url => {
    window.history.pushState({}, '', url);
    const u = new URL(url, window.location.origin);
    setRoute(routeFromLocation(u.pathname, u.search));
    window.scrollTo({ top: 0 });
  }, []);

  useEffect(() => {
    const onPop = () => setRoute(routeFromLocation(window.location.pathname, window.location.search));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  useEffect(() => { tradeApi.me().then(setMe); }, []);
  useEffect(() => {
    if (route.view === 'requests') setContext('My requests');
    if (route.view === 'im') setContext('Investment Management');
  }, [route]);
  useEffect(() => { document.title = context ? `${context} · Trade ticket` : 'Trade ticket'; }, [context]);

  // Replacing a request starts from that request, so load it first.
  useEffect(() => {
    if (route.view !== 'replace') { setReplacing(null); return; }
    let live = true;
    tradeApi.request(route.id).then(res => {
      if (!live) return;
      setReplacing(res.ok ? { ...res.body.request, canReply: res.body.canReply } : { failed: res.body.error });
    });
    return () => { live = false; };
  }, [route]);

  let body;
  if (me && !me.ok) {
    body = (
      <div className="tt-page">
        <div className="tt-banner is-error" role="alert"><strong>Salesforce did not let the app sign you in.</strong> {me.body.error}</div>
      </div>
    );
  } else if (route.view === 'new') {
    body = <NewTicket key={route.householdId} householdId={route.householdId} onHousehold={setContext} navigate={navigate} />;
  } else if (route.view === 'request') {
    body = <RequestDetail key={route.id} id={route.id} navigate={navigate} onLoaded={r => setContext(`${r.name} · ${r.household}`)} />;
  } else if (route.view === 'im') {
    body = <ImQueue navigate={navigate} />;
  } else if (route.view === 'imRequest') {
    body = <ImRequest key={route.id} id={route.id} navigate={navigate} onLoaded={r => setContext(`${r.name} · ${r.household}`)} />;
  } else if (route.view === 'replace') {
    if (!replacing) body = <div className="tt-loading"><div className="load-spinner" /><p>Reading the request from Salesforce…</p></div>;
    else if (replacing.failed) body = <div className="tt-page"><div className="tt-banner is-error" role="alert">{replacing.failed}</div></div>;
    else if (!replacing.canReply) {
      body = (
        <div className="tt-page">
          <div className="tt-banner is-error" role="alert">
            {replacing.name} is not waiting on you, so it cannot be replaced from here. Its status is {replacing.status}.
          </div>
        </div>
      );
    } else {
      body = <NewTicket key={`replace-${replacing.id}`} householdId={replacing.householdId} replacing={replacing} onHousehold={setContext} navigate={navigate} />;
    }
  } else {
    body = <MyRequests navigate={navigate} />;
  }

  return (
    <div className="shell tt-shell">
      <Topbar me={me} context={context} navigate={navigate} />
      <main className="tt-main">{body}</main>
    </div>
  );
}
